"use client";

import React, { FC, useEffect, useRef, useState } from "react";
import { CaretDownLine, CaretDownSolid, CheckMarkThin } from "@/icons";
import {
  Field,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  ListboxProps,
} from "@headlessui/react";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";
import cn from "classnames";
import { twMerge } from "@/helpers/twMerge";
import { Dropdown } from "@/types/General/Dropdown";
import {
  RectangularFormField,
  RoundFormField,
  RoundFormFieldProps,
  StandardFormField,
  StandardFormFieldProps,
} from "@/components/FormFieldRendering";
import { Input } from "@/components/Input";

const getDropdownCompareValue = (
  item: Dropdown | Dropdown[] | null | undefined,
) =>
  Array.isArray(item)
    ? item.map((option) => option.value).join("\u0000")
    : JSON.stringify(item?.value);

export type BaseSelectProps = {
  className?: string;
  options: Dropdown[];
  multipleSelectionDisplayLabel?: string;
  required?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  optionsContainerClassName?: string;
  formControlContainerClassName?: string;
  optionClassName?: string;
  afterDecoration?: FC<{ open: boolean }>;
  selectedDecoration?: FC<{ selected: boolean; option: Dropdown }>;
  onBlur?: () => void;
} & ListboxProps<"div", Dropdown | Dropdown[] | null>;
// TODO: Refactor to use discriminated union pattern based on the `multiple` prop
// (e.g. having Dropdown in one type, Dropdown[] and multipleSelectionDisplayLabel in other).
// This is critical for type safety - currently the component accepts incompatible prop combinations.
// Consider implementing during WEBDEV-45717 (Port Dropdown logic to Select):
// https://swicloud.atlassian.net/browse/WEBDEV-45717

const BaseSelect: FC<BaseSelectProps> = ({
  options = [],
  multipleSelectionDisplayLabel,
  required = false,
  className,
  optionClassName,
  optionsContainerClassName,
  selectedDecoration: SelectedDecoration,
  afterDecoration: AfterDecoration,
  onBlur,
  ...props
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [theme, setTheme] = useState<string | undefined>(undefined);

  useEffect(() => {
    setTheme(
      buttonRef.current
        ?.closest('[data-theme]:not([data-theme=""])')
        ?.getAttribute("data-theme") ?? undefined,
    );
  }, []);

  // not using onBlur prop so that `el.dispatchEvent(new FocusEvent('blur'));` is working from Builder in OnSubmit for validation
  useEffect(() => {
    const button = buttonRef.current;
    if (!button || !onBlur) {
      return;
    }
    button.addEventListener("blur", onBlur);
    return () => {
      button.removeEventListener("blur", onBlur);
    };
  }, [onBlur]);

  return (
    <Listbox
      as="div"
      className="w-full"
      by={(a, b) => getDropdownCompareValue(a) === getDropdownCompareValue(b)}
      aria-multiselectable={props.multiple ? "true" : "false"}
      {...props}
    >
      {({ open }) => {
        return (
          <>
            <ListboxButton
              ref={buttonRef}
              className={twMerge(
                "disabled:bg-swdc-gray-50 border-swdc-gray-200 text-body-primary relative flex w-full shrink-0 appearance-none items-center justify-between text-left outline-0 disabled:cursor-not-allowed",
                className,
              )}
              role="combobox"
              aria-required={required}
            >
              {({ value }) => {
                const isArray = Array.isArray(value);
                const isPlaceholder = isArray
                  ? // if we have empty array we show placeholder should be first option as disabled
                    value.length === 0 && options?.[0]?.disabled
                  : // if it's not array we will have it as value with disabled
                    value?.disabled;
                let displayText = "";

                if (isArray) {
                  if (value.length === 0) {
                    displayText = options[0]?.label || options[0]?.value;
                  }
                  if (multipleSelectionDisplayLabel && value.length > 0) {
                    displayText = multipleSelectionDisplayLabel;
                  }
                } else {
                  displayText = value?.label || value?.value;
                }

                return (
                  <>
                    <span
                      className={cn(
                        "relative flex truncate",
                        isPlaceholder && "text-body-secondary",
                        !props.multiple &&
                          isPlaceholder &&
                          required &&
                          "after:text-swdc-red-500 after:content-['*'/'']",
                      )}
                    >
                      {displayText}
                    </span>
                    {AfterDecoration && <AfterDecoration open={open} />}
                  </>
                );
              }}
            </ListboxButton>
            <ListboxOptions
              data-theme={theme}
              className={twMerge(
                "border-swdc-gray-200 bg-background text-tab-link shadow-dropdown z-40 overflow-hidden border focus:outline-none",
                optionsContainerClassName,
              )}
              anchor="bottom start"
              transition
              unmount={false}
              aria-required={required}
            >
              <ul className="max-h-75 overflow-y-auto">
                {options.map((option, ix) => (
                  <ListboxOption
                    as="li"
                    value={option}
                    key={`${option.value}-${ix}`}
                    disabled={option.disabled}
                    aria-label={option.ariaLabel || option.label}
                    className={twMerge(
                      "text-body-m text-tab-link hover:not-data-disabled:bg-background-highlight data-focus:not-data-disabled:outline-swdc-blue-400 data-selected:not-data-disabled:bg-swdc-silver-200 gray:data-selected:not-data-disabled:bg-swdc-silver-300 black:data-selected:not-data-disabled:bg-swdc-cool-gray-600 data-disabled:text-body-secondary hover:data-selected:not-data-disabled:bg-background-highlight hover:data-selected:not-data-disabled:outline-swdc-silver-200 gray:hover:data-selected:not-data-disabled:outline-swdc-silver-300 black:hover:data-selected:not-data-disabled:outline-swdc-cool-gray-600 relative w-full cursor-pointer px-2 py-2 select-none data-disabled:cursor-not-allowed data-disabled:border-none data-disabled:bg-transparent data-disabled:outline-none data-focus:not-data-disabled:ring-3 data-focus:not-data-disabled:ring-white data-focus:not-data-disabled:outline-2 data-focus:not-data-disabled:-outline-offset-2 data-focus:not-data-disabled:ring-inset hover:data-focus:ring-0 hover:data-focus:outline-0 data-selected:not-data-disabled:font-bold hover:data-selected:not-data-disabled:ring-3 hover:data-selected:not-data-disabled:ring-white hover:data-selected:not-data-disabled:outline-2 hover:data-selected:not-data-disabled:-outline-offset-2 hover:data-selected:not-data-disabled:ring-inset data-selected:data-disabled:data-focus:outline-none lg:px-4",
                      optionClassName,
                    )}
                    {...getDataAutomationId(`option-${ix}`)}
                    {...getDataLinkAttributes({
                      dld: option.dld,
                      dlt: option.dlt,
                    })}
                  >
                    {({ selected }) =>
                      SelectedDecoration ? (
                        <SelectedDecoration
                          selected={selected}
                          option={option}
                        />
                      ) : (
                        <>{option.label}</>
                      )
                    }
                  </ListboxOption>
                ))}
              </ul>
            </ListboxOptions>
          </>
        );
      }}
    </Listbox>
  );
};
const SELECTED_DECORATION_MAPPING: {
  [key in "checkmark" | "checkbox" | "radio"]: FC<{
    selected: boolean;
    option: Dropdown;
  }>;
} = {
  checkmark: ({ selected, option }) => (
    <div className="flex flex-row items-center justify-between">
      {option.label}
      {selected && !option.disabled && (
        <CheckMarkThin className="size-4.5" aria-hidden />
      )}
    </div>
  ),
  checkbox: ({ selected, option }) => (
    <Field className="flex flex-row items-center gap-x-1.5">
      <Input
        type="checkbox"
        variant="without-label"
        checked={selected}
        disabled={option.disabled}
        className="border-swdc-gray-400 bg-swdc-white data-active:border-swdc-blue-300 data-checked:border-swdc-blue-500 data-disabled:border-swdc-gray-200 data-disabled:data-checked:bg-swdc-gray-200 group-active:border-swdc-blue-300 data-checked:bg-swdc-blue-500 pointer-events-none flex aspect-square size-4 items-center justify-center border border-solid data-active:border-2 data-disabled:border data-checked:data-disabled:opacity-75"
      />
      {option.label}
    </Field>
  ),
  radio: ({ selected, option }) => (
    <Field className="flex flex-row items-center gap-x-1.5">
      <Input
        readOnly
        type="radio"
        variant="without-label"
        checked={selected}
        disabled={option.disabled}
        className="disabled:border-swdc-gray-200 group-active:not-disabled:border-swdc-blue-300 checked:border-swdc-blue-500 pointer-events-none size-4 appearance-none rounded-full border group-hover:not-checked:border-1 checked:border-5 active:not-checked:not-disabled:border-2 disabled:opacity-75"
      />
      {option.label}
    </Field>
  ),
};

export type SelectProps = (
  | ({
      variant: "form";
    } & Omit<
      StandardFormFieldProps<BaseSelectProps>,
      "formControl" | "withFixedLabel" | "selectedDecoration"
    >)
  | ({
      variant: "round";
    } & Omit<
      RoundFormFieldProps<BaseSelectProps>,
      "formControl" | "selectedDecoration"
    >)
  | ({
      variant: "rectangular";
    } & Omit<
      RectangularFormField<BaseSelectProps>,
      "formControl" | "selectedDecoration"
    >)
  | ({
      variant: "large rectangular";
    } & Omit<BaseSelectProps, "selectedDecoration">)
) & {
  selectedDecoration?: "checkmark" | "checkbox" | "radio";
};
export const Select: FC<SelectProps> = ({
  variant,
  selectedDecoration = "checkmark",
  ...props
}) => {
  switch (variant) {
    case "form":
      return (
        <StandardFormField<HTMLDivElement, BaseSelectProps>
          className="w-full outline-none"
          optionClassName="first:rounded-t-2xl last:rounded-b-2xl"
          optionsContainerClassName="w-(--button-width) [--anchor-gap:var(--spacing-4)] rounded-2xl"
          formControl={BaseSelect}
          withFixedLabel={true}
          selectedDecoration={SELECTED_DECORATION_MAPPING[selectedDecoration]}
          afterDecoration={
            !props.disabled
              ? ({ open }) => (
                  <CaretDownSolid
                    height={14}
                    fill="#666"
                    className={cn("flex text-sm", open && "rotate-180")}
                    aria-hidden
                  />
                )
              : undefined
          }
          {...props}
        />
      );
    case "round":
      return (
        <RoundFormField<BaseSelectProps>
          className="rounded-button w-full px-4 py-1.5 outline-none"
          optionClassName="first:rounded-t-2xl last:rounded-b-2xl"
          optionsContainerClassName="w-(--button-width) [--anchor-gap:var(--spacing-4)] rounded-2xl"
          formControl={BaseSelect}
          {...props}
          selectedDecoration={SELECTED_DECORATION_MAPPING[selectedDecoration]}
          afterDecoration={
            !props.disabled
              ? ({ open }) => (
                  <CaretDownSolid
                    height={14}
                    fill="#666"
                    className={cn("flex text-sm", open && "rotate-180")}
                    aria-hidden
                  />
                )
              : undefined
          }
        />
      );
    case "rectangular":
      return (
        <RectangularFormField<BaseSelectProps>
          className="w-full px-4 py-2"
          formControl={BaseSelect}
          optionClassName="group active:text-swdc-blue-500 active:bg-swdc-white!"
          optionsContainerClassName="w-(--button-width) [--anchor-gap:var(--spacing-1)] rounded-corner-subtle"
          afterDecoration={
            !props.disabled
              ? ({ open }) => (
                  <CaretDownLine
                    height={20}
                    fill="var(--color-tab-link)"
                    className={cn("ml-2 flex min-w-6", open && "rotate-180")}
                    aria-hidden
                  />
                )
              : undefined
          }
          selectedDecoration={SELECTED_DECORATION_MAPPING[selectedDecoration]}
          {...props}
        />
      );
    case "large rectangular":
      return (
        <BaseSelect
          className="bg-background-highlight text-subhead-2xs sm:text-subhead-s text-button-tertiary-content border p-4 leading-7 font-medium outline-2 outline-transparent sm:leading-8"
          optionsContainerClassName="w-[calc(var(--button-width)+1rem)] [--anchor-offset:-0.5rem] bg-background text-tab-link inset-x-0 mt-3"
          afterDecoration={
            !props.disabled
              ? ({ open }) => (
                  <CaretDownLine
                    fill="var(--color-tab-link)"
                    className={cn("flex", open && "rotate-180")}
                    aria-hidden
                  />
                )
              : undefined
          }
          selectedDecoration={
            selectedDecoration
              ? SELECTED_DECORATION_MAPPING[selectedDecoration]
              : undefined
          }
          {...props}
        />
      );
  }
};
